import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Loader2, TrendingUp, TrendingDown, UserX, Calculator, Landmark, Play, History } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";

const ICONS = { TrendingUp, TrendingDown, UserX, Calculator, Landmark };

const fmtGhs = (n) => {
  if (n == null) return "—";
  if (Math.abs(n) >= 1_000_000) return `GH₵ ${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1000) return `GH₵ ${(n / 1000).toFixed(1)}K`;
  return `GH₵ ${n.toLocaleString()}`;
};

export default function Simulation() {
  const [catalog, setCatalog] = useState([]);
  const [scenario, setScenario] = useState(null);
  const [params, setParams] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/simulation/catalog").then((r) => {
      setCatalog(r.data);
      if (r.data.length) {
        setScenario(r.data[0]);
        setParams(Object.fromEntries(r.data[0].params.map((p) => [p.key, p.default])));
      }
    });
    api.get("/simulation/history").then((r) => setHistory(r.data));
  }, []);

  const pickScenario = (s) => {
    setScenario(s);
    setParams(Object.fromEntries(s.params.map((p) => [p.key, p.default])));
    setResult(null);
  };

  const run = async () => {
    if (!scenario) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/simulation/run", { scenario: scenario.id, params });
      setResult(r.data);
      setHistory((h) => [r.data, ...h]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="simulation-page">
      {/* Header */}
      <div>
        <div className="label-eyebrow mb-1">// AI Strategic Simulation</div>
        <h1 className="font-display text-3xl text-white tracking-tight">Scenario Engine</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Stress-test institutional outcomes against market, conversion, stakeholder, budget and policy shocks. Each
          simulation pairs a deterministic GHS model with a Claude Sonnet 4.5 executive read.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Scenario picker */}
        <div className="lg:col-span-3 space-y-2" data-testid="scenario-list">
          <div className="label-eyebrow mb-2">// Scenarios</div>
          {catalog.map((s) => {
            const Icon = ICONS[s.icon] || TrendingUp;
            const active = scenario?.id === s.id;
            return (
              <button
                key={s.id}
                onClick={() => pickScenario(s)}
                data-testid={`scenario-${s.id}`}
                className={`w-full text-left p-3 border transition-all ${
                  active ? "border-[#D4AF37]/60 bg-[#D4AF37]/8" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div className="flex items-start gap-2">
                  <Icon className="w-3.5 h-3.5 mt-0.5" style={{ color: active ? "var(--gold-400)" : "#94A3B8" }} strokeWidth={1.6} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium">{s.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{s.tagline}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Configurator */}
        <div className="lg:col-span-4 nexus-glass-panel p-5" data-testid="scenario-config">
          {scenario && (
            <>
              <div className="label-eyebrow mb-2">// Configure {scenario.name}</div>
              <h2 className="font-display text-lg text-white mb-4">{scenario.tagline}</h2>
              <div className="space-y-4">
                {scenario.params.map((p) => (
                  <div key={p.key}>
                    <label className="label-eyebrow block mb-1.5">{p.label}</label>
                    {p.type === "textarea" ? (
                      <textarea
                        rows={4}
                        data-testid={`param-${p.key}`}
                        value={params[p.key] ?? ""}
                        onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                        className="w-full bg-[#0A111F] border border-white/10 text-white text-sm font-body p-2.5 focus:border-[#D4AF37]/60 focus:outline-none resize-none"
                        placeholder="Describe the policy shock or regulatory change…"
                      />
                    ) : (
                      <input
                        type="number"
                        step={p.step}
                        min={p.min}
                        max={p.max}
                        data-testid={`param-${p.key}`}
                        value={params[p.key] ?? ""}
                        onChange={(e) => setParams({ ...params, [p.key]: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-[#0A111F] border border-white/10 text-white text-sm font-mono p-2.5 focus:border-[#D4AF37]/60 focus:outline-none"
                      />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={run}
                disabled={running}
                data-testid="run-simulation-btn"
                className="mt-6 w-full py-3 flex items-center justify-center gap-2 font-medium text-sm transition-all disabled:opacity-60"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" strokeWidth={2} />}
                {running ? "Simulating…" : "Run simulation"}
              </button>
            </>
          )}
        </div>

        {/* Result */}
        <div className="lg:col-span-5 space-y-4" data-testid="scenario-result">
          {!result && !running && (
            <div className="nexus-glass-panel p-8 text-center">
              <div className="label-eyebrow mb-2">// Awaiting input</div>
              <p className="text-slate-400 text-sm">Configure a scenario and run the simulation. Results appear here with an AI executive read.</p>
            </div>
          )}
          {running && (
            <div className="nexus-glass-panel p-8 text-center flex items-center justify-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--gold-400)" }} />
              <span className="text-sm text-slate-400 font-mono tracking-widest">RUNNING SIMULATION</span>
            </div>
          )}
          {result && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="nexus-glass-panel p-5">
                <div className="label-eyebrow">// Deterministic Output</div>
                <h2 className="font-display text-xl text-white mt-1 mb-4 leading-snug">{result.simulation?.headline}</h2>
                {result.simulation?.series && (
                  <div className="space-y-2">
                    {result.simulation.series.map((row) => (
                      <div key={row.year} className="flex items-center justify-between font-mono text-[12px] border-b border-white/5 py-1.5">
                        <span className="text-slate-400">Year {row.year}</span>
                        <span className="text-white">{fmtGhs(row.pipeline_ghs)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {Object.entries(result.simulation || {})
                    .filter(([k, v]) => !["headline", "series", "qualitative"].includes(k) && (typeof v === "number" || typeof v === "string"))
                    .map(([k, v]) => (
                      <div key={k} className="p-3 border border-white/5 bg-white/[0.02]">
                        <div className="label-eyebrow">{k.replace(/_/g, " ")}</div>
                        <div className="font-mono text-sm text-white mt-1">
                          {typeof v === "number" ? (k.includes("ghs") ? fmtGhs(v) : v.toLocaleString()) : String(v)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {result.narrative && (
                <div className="nexus-glass-panel p-5" data-testid="simulation-narrative">
                  <div className="label-eyebrow mb-2">// Executive Read</div>
                  <div className="nexus-prose text-sm">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.narrative}</ReactMarkdown>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-3 h-3 text-slate-500" />
            <span className="label-eyebrow">// Recent simulations</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {history.slice(0, 6).map((h) => (
              <button
                key={h.id}
                onClick={() => setResult(h)}
                data-testid={`history-${h.id}`}
                className="text-left p-4 nexus-card hover:border-[#D4AF37]/30"
              >
                <div className="label-eyebrow">{h.scenario.replace(/_/g, " ")}</div>
                <div className="text-sm text-white mt-1 leading-snug line-clamp-2">{h.simulation?.headline}</div>
                <div className="font-mono text-[10px] text-slate-500 mt-2 tracking-widest">
                  {new Date(h.created_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
